import React, { useState } from 'react';
import {
  View,
  StyleSheet,
  TouchableOpacity,
  Platform,
  ScrollView,
  Alert,
  TextInput,
} from 'react-native';
import Text from '../../common/Text';
import Modal from '../../common/Modal';
import { useTheme } from '../../../hooks/useTheme';
import { palette } from '../../../utils/palette';
import {
  useSupportTickets,
  useRespondToTicket,
  useUpdateTicketStatus,
  useAnnouncements,
  useCreateAnnouncement,
  useFAQs,
  useCreateFAQ,
  useUpdateFAQ,
  useDeleteFAQ,
} from '../../../hooks/useAdminData';
import { Ionicons } from '@expo/vector-icons';
import SearchBar from '../../shared/SearchBar';
import SupportTicketsGrid from './SupportTicketsGrid';

const SupportView: React.FC = () => {
  const { colors } = useTheme();
  const [activeTab, setActiveTab] = useState<
    'tickets' | 'announcements' | 'faqs'
  >('tickets');
  const [ticketsPagination, _setTicketsPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 1,
  });
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [priorityFilter, setPriorityFilter] = useState<string>('');
  const [ticketsSearch, setTicketsSearch] = useState('');

  // Selected ticket for response
  const [selectedTicket, setSelectedTicket] = useState<any>(null);
  const [responseText, setResponseText] = useState('');
  const [responseModalVisible, setResponseModalVisible] = useState(false);

  // Announcement form
  const [announcementFormVisible, setAnnouncementFormVisible] = useState(false);
  const [announcementTitle, setAnnouncementTitle] = useState('');
  const [announcementMessage, setAnnouncementMessage] = useState('');
  const [announcementType, setAnnouncementType] = useState('info');
  const [announcementStartsAt, setAnnouncementStartsAt] = useState('');
  const [announcementEndsAt, setAnnouncementEndsAt] = useState('');
  const [announcementCtaText, setAnnouncementCtaText] = useState('');
  const [announcementCtaUrl, setAnnouncementCtaUrl] = useState('');
  const [announcementImageUrl, setAnnouncementImageUrl] = useState('');

  // FAQ form
  const [faqFormVisible, setFaqFormVisible] = useState(false);
  const [editingFaq, setEditingFaq] = useState<any>(null);
  const [faqQuestion, setFaqQuestion] = useState('');
  const [faqAnswer, setFaqAnswer] = useState('');
  const [faqCategory, setFaqCategory] = useState('');
  const [faqOrder, setFaqOrder] = useState('0');

  const { data: ticketsData, isLoading: ticketsLoading } = useSupportTickets(
    ticketsPagination,
    {
      status: statusFilter || undefined,
      priority: priorityFilter || undefined,
    },
    { enabled: activeTab === 'tickets' }
  );

  const { data: announcementsData, isLoading: announcementsLoading } =
    useAnnouncements(false, {
      enabled: activeTab === 'announcements',
    });

  const { data: faqsData, isLoading: faqsLoading } = useFAQs(false, {
    enabled: activeTab === 'faqs',
  });

  const respondToTicketMutation = useRespondToTicket();
  const updateTicketStatusMutation = useUpdateTicketStatus();
  const createAnnouncementMutation = useCreateAnnouncement();
  const createFaqMutation = useCreateFAQ();
  const updateFaqMutation = useUpdateFAQ();
  const deleteFaqMutation = useDeleteFAQ();

  const tickets = ticketsData?.items || [];
  const announcements = announcementsData?.items || [];
  const faqs = faqsData?.items || [];

  const handleRespondToTicket = () => {
    if (!responseText.trim()) {
      Alert.alert('Error', 'Please enter a response');
      return;
    }

    respondToTicketMutation.mutate(
      { ticketId: selectedTicket.id, response: responseText },
      {
        onSuccess: () => {
          setResponseModalVisible(false);
          setResponseText('');
          setSelectedTicket(null);
        },
      }
    );
  };

  const handleUpdateTicketStatus = (ticketId: number, newStatus: string) => {
    updateTicketStatusMutation.mutate({ ticketId, status: newStatus });
  };

  const handleCreateAnnouncement = () => {
    if (!announcementTitle.trim() || !announcementMessage.trim()) {
      Alert.alert('Error', 'Please fill in all required fields');
      return;
    }

    createAnnouncementMutation.mutate(
      {
        title: announcementTitle,
        message: announcementMessage,
        type: announcementType,
        starts_at: announcementStartsAt || undefined,
        ends_at: announcementEndsAt || undefined,
        call_to_action_text: announcementCtaText || undefined,
        call_to_action_url: announcementCtaUrl || undefined,
        image_url: announcementImageUrl || undefined,
      },
      {
        onSuccess: () => {
          setAnnouncementFormVisible(false);
          setAnnouncementTitle('');
          setAnnouncementMessage('');
          setAnnouncementType('info');
          setAnnouncementStartsAt('');
          setAnnouncementEndsAt('');
          setAnnouncementCtaText('');
          setAnnouncementCtaUrl('');
          setAnnouncementImageUrl('');
        },
      }
    );
  };

  const handleCreateFAQ = () => {
    if (!faqQuestion.trim() || !faqAnswer.trim()) {
      Alert.alert('Error', 'Please fill in question and answer');
      return;
    }

    if (editingFaq) {
      updateFaqMutation.mutate(
        {
          faqId: editingFaq.id,
          data: {
            question: faqQuestion,
            answer: faqAnswer,
            category: faqCategory || undefined,
            order: parseInt(faqOrder) || 0,
          },
        },
        {
          onSuccess: () => {
            setFaqFormVisible(false);
            resetFaqForm();
          },
        }
      );
    } else {
      createFaqMutation.mutate(
        {
          question: faqQuestion,
          answer: faqAnswer,
          category: faqCategory || undefined,
          order: parseInt(faqOrder) || 0,
        },
        {
          onSuccess: () => {
            setFaqFormVisible(false);
            resetFaqForm();
          },
        }
      );
    }
  };

  const resetFaqForm = () => {
    setFaqQuestion('');
    setFaqAnswer('');
    setFaqCategory('');
    setFaqOrder('0');
    setEditingFaq(null);
  };

  const handleEditFAQ = (faq: any) => {
    setEditingFaq(faq);
    setFaqQuestion(faq.question);
    setFaqAnswer(faq.answer);
    setFaqCategory(faq.category || '');
    setFaqOrder(String(faq.order || 0));
    setFaqFormVisible(true);
  };

  const handleDeleteFAQ = (faqId: number) => {
    Alert.alert('Confirm Delete', 'Are you sure you want to delete this FAQ?', [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Delete',
        style: 'destructive',
        onPress: () => deleteFaqMutation.mutate(faqId),
      },
    ]);
  };

  return (
    <ScrollView
      style={[styles.container, { backgroundColor: colors.background }]}
    >
      {/* Tabs */}
      <View style={[styles.tabs, { borderBottomColor: colors.border }]}>
        <TouchableOpacity
          style={[
            styles.tab,
            activeTab === 'tickets' && {
              borderBottomColor: colors.primary,
              borderBottomWidth: 2,
            },
          ]}
          onPress={() => setActiveTab('tickets')}
        >
          <Text
            style={[
              styles.tabText,
              {
                color:
                  activeTab === 'tickets' ? colors.primary : colors.secondary,
              },
            ]}
          >
            Support Tickets
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[
            styles.tab,
            activeTab === 'announcements' && {
              borderBottomColor: colors.primary,
              borderBottomWidth: 2,
            },
          ]}
          onPress={() => setActiveTab('announcements')}
        >
          <Text
            style={[
              styles.tabText,
              {
                color:
                  activeTab === 'announcements'
                    ? colors.primary
                    : colors.secondary,
              },
            ]}
          >
            Announcements
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[
            styles.tab,
            activeTab === 'faqs' && {
              borderBottomColor: colors.primary,
              borderBottomWidth: 2,
            },
          ]}
          onPress={() => setActiveTab('faqs')}
        >
          <Text
            style={[
              styles.tabText,
              {
                color: activeTab === 'faqs' ? colors.primary : colors.secondary,
              },
            ]}
          >
            FAQs
          </Text>
        </TouchableOpacity>
      </View>

      {/* Support Tickets Tab */}
      {activeTab === 'tickets' && (
        <View style={styles.tabContent}>
          <SearchBar
            value={ticketsSearch}
            onChangeText={setTicketsSearch}
            placeholder="Search tickets..."
            containerStyle={styles.searchBarContainer}
          />

          {Platform.OS === 'web' && (
            <View
              style={[styles.filterRow, { borderBottomColor: colors.border }]}
            >
              <View style={styles.filterGroup}>
                <Text style={[styles.filterLabel, { color: colors.text }]}>
                  Status:
                </Text>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  style={{
                    padding: '6px 10px',
                    borderRadius: '6px',
                    border: `1px solid ${colors.border}`,
                    backgroundColor: colors.card,
                    color: colors.text,
                    fontSize: '12px',
                    marginLeft: '8px',
                  }}
                >
                  <option value="">All</option>
                  <option value="open">Open</option>
                  <option value="in_progress">In Progress</option>
                  <option value="resolved">Resolved</option>
                  <option value="closed">Closed</option>
                </select>
              </View>
              <View style={styles.filterGroup}>
                <Text style={[styles.filterLabel, { color: colors.text }]}>
                  Priority:
                </Text>
                <select
                  value={priorityFilter}
                  onChange={(e) => setPriorityFilter(e.target.value)}
                  style={{
                    padding: '6px 10px',
                    borderRadius: '6px',
                    border: `1px solid ${colors.border}`,
                    backgroundColor: colors.card,
                    color: colors.text,
                    fontSize: '12px',
                    marginLeft: '8px',
                  }}
                >
                  <option value="">All</option>
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </View>
            </View>
          )}

          {ticketsLoading ? (
            <Text style={[styles.loading, { color: colors.secondary }]}>
              Loading tickets...
            </Text>
          ) : tickets.length === 0 ? (
            <Text style={[styles.empty, { color: colors.secondary }]}>
              {ticketsSearch
                ? 'No tickets found matching your search'
                : 'No support tickets'}
            </Text>
          ) : (
            <SupportTicketsGrid
              tickets={tickets}
              searchValue={ticketsSearch}
              onRespond={(ticketId) => {
                const ticket = tickets.find((t) => t.id === ticketId);
                if (ticket) {
                  setSelectedTicket(ticket);
                  setResponseModalVisible(true);
                }
              }}
              onUpdateStatus={handleUpdateTicketStatus}
            />
          )}
        </View>
      )}

      {/* Announcements Tab */}
      {activeTab === 'announcements' && (
        <View style={styles.tabContent}>
          <View style={styles.sectionHeader}>
            <Text style={[styles.sectionTitle, { color: colors.text }]}>
              System Announcements
            </Text>
            <TouchableOpacity
              style={[styles.addButton, { backgroundColor: colors.primary }]}
              onPress={() => setAnnouncementFormVisible(true)}
            >
              <Ionicons name="add" size={20} color={palette.white} />
              <Text style={[styles.addButtonText, { color: palette.white }]}>
                New Announcement
              </Text>
            </TouchableOpacity>
          </View>

          {announcementsLoading ? (
            <Text style={[styles.loading, { color: colors.secondary }]}>
              Loading announcements...
            </Text>
          ) : announcements.length === 0 ? (
            <Text style={[styles.empty, { color: colors.secondary }]}>
              No announcements
            </Text>
          ) : (
            <View style={styles.list}>
              {announcements.map((announcement: any) => (
                <View
                  key={announcement.id}
                  style={[
                    styles.announcementCard,
                    {
                      backgroundColor: colors.card,
                      borderColor: colors.border,
                    },
                  ]}
                >
                  <View style={styles.announcementHeader}>
                    <Text
                      style={[styles.announcementTitle, { color: colors.text }]}
                    >
                      {announcement.title}
                    </Text>
                    <View
                      style={[
                        styles.badge,
                        { backgroundColor: getTypeColor(announcement.type) },
                      ]}
                    >
                      <Text style={styles.badgeText}>{announcement.type}</Text>
                    </View>
                  </View>
                  <Text
                    style={[styles.announcementMessage, { color: colors.text }]}
                  >
                    {announcement.message}
                  </Text>
                  <Text
                    style={[
                      styles.announcementMeta,
                      { color: colors.secondary },
                    ]}
                  >
                    Target: {announcement.target_audience} • Active:{' '}
                    {announcement.is_active ? 'Yes' : 'No'}
                  </Text>
                </View>
              ))}
            </View>
          )}
        </View>
      )}

      {/* FAQs Tab */}
      {activeTab === 'faqs' && (
        <View style={styles.tabContent}>
          <View style={styles.sectionHeader}>
            <Text style={[styles.sectionTitle, { color: colors.text }]}>
              Frequently Asked Questions
            </Text>
            <TouchableOpacity
              style={[styles.addButton, { backgroundColor: colors.primary }]}
              onPress={() => {
                resetFaqForm();
                setFaqFormVisible(true);
              }}
            >
              <Ionicons name="add" size={20} color={palette.white} />
              <Text style={[styles.addButtonText, { color: palette.white }]}>
                New FAQ
              </Text>
            </TouchableOpacity>
          </View>

          {faqsLoading ? (
            <Text style={[styles.loading, { color: colors.secondary }]}>
              Loading FAQs...
            </Text>
          ) : faqs.length === 0 ? (
            <Text style={[styles.empty, { color: colors.secondary }]}>
              No FAQs
            </Text>
          ) : (
            <View style={styles.list}>
              {faqs.map((faq: any) => (
                <View
                  key={faq.id}
                  style={[
                    styles.faqCard,
                    {
                      backgroundColor: colors.card,
                      borderColor: colors.border,
                    },
                  ]}
                >
                  <View style={styles.faqHeader}>
                    <Text style={[styles.faqQuestion, { color: colors.text }]}>
                      {faq.question}
                    </Text>
                    <View style={styles.faqActions}>
                      <TouchableOpacity
                        onPress={() => handleEditFAQ(faq)}
                        style={styles.iconButton}
                      >
                        <Ionicons
                          name="pencil"
                          size={20}
                          color={colors.primary}
                        />
                      </TouchableOpacity>
                      <TouchableOpacity
                        onPress={() => handleDeleteFAQ(faq.id)}
                        style={styles.iconButton}
                      >
                        <Ionicons
                          name="trash"
                          size={20}
                          color={colors.errorText || palette.brightMatteRed}
                        />
                      </TouchableOpacity>
                    </View>
                  </View>
                  <Text style={[styles.faqAnswer, { color: colors.text }]}>
                    {faq.answer}
                  </Text>
                  {faq.category && (
                    <Text
                      style={[styles.faqCategory, { color: colors.secondary }]}
                    >
                      Category: {faq.category}
                    </Text>
                  )}
                </View>
              ))}
            </View>
          )}
        </View>
      )}

      {/* Response Modal */}
      <Modal
        visible={responseModalVisible}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setResponseModalVisible(false)}
      >
        <View
          style={[
            styles.modalOverlay,
            { backgroundColor: 'rgba(0, 0, 0, 0.5)' },
          ]}
        >
          <View
            style={[
              styles.modalContent,
              { backgroundColor: colors.background },
            ]}
          >
            <View
              style={[styles.modalHeader, { borderBottomColor: colors.border }]}
            >
              <Text style={[styles.modalTitle, { color: colors.text }]}>
                Respond to Ticket
              </Text>
              <TouchableOpacity onPress={() => setResponseModalVisible(false)}>
                <Ionicons name="close" size={24} color={colors.text} />
              </TouchableOpacity>
            </View>
            {selectedTicket && (
              <>
                <Text style={[styles.modalLabel, { color: colors.text }]}>
                  Subject: {selectedTicket.subject}
                </Text>
                <Text style={[styles.modalLabel, { color: colors.text }]}>
                  Message:
                </Text>
                <Text
                  style={[styles.modalMessage, { color: colors.secondary }]}
                >
                  {selectedTicket.message}
                </Text>
                <Text style={[styles.modalLabel, { color: colors.text }]}>
                  Your Response:
                </Text>
                <TextInput
                  style={[
                    styles.responseInput,
                    {
                      backgroundColor: colors.card,
                      color: colors.text,
                      borderColor: colors.border,
                    },
                  ]}
                  value={responseText}
                  onChangeText={setResponseText}
                  placeholder="Enter your response..."
                  multiline
                  numberOfLines={6}
                  placeholderTextColor={colors.secondary}
                />
                <View style={styles.modalActions}>
                  <TouchableOpacity
                    style={[
                      styles.modalButton,
                      { backgroundColor: colors.secondary },
                    ]}
                    onPress={() => setResponseModalVisible(false)}
                  >
                    <Text
                      style={[styles.modalButtonText, { color: palette.white }]}
                    >
                      Cancel
                    </Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[
                      styles.modalButton,
                      { backgroundColor: colors.primary },
                    ]}
                    onPress={handleRespondToTicket}
                  >
                    <Text
                      style={[styles.modalButtonText, { color: palette.white }]}
                    >
                      Send Response
                    </Text>
                  </TouchableOpacity>
                </View>
              </>
            )}
          </View>
        </View>
      </Modal>

      {/* Announcement Form Modal */}
      <Modal
        visible={announcementFormVisible}
        onClose={() => setAnnouncementFormVisible(false)}
        title="Create Announcement"
        showCloseButton={true}
        footer={
          <View style={styles.modalActions}>
            <TouchableOpacity
              style={[
                styles.modalButton,
                { backgroundColor: colors.secondary },
              ]}
              onPress={() => setAnnouncementFormVisible(false)}
            >
              <Text style={[styles.modalButtonText, { color: palette.white }]}>
                Cancel
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.modalButton, { backgroundColor: colors.primary }]}
              onPress={handleCreateAnnouncement}
            >
              <Text style={[styles.modalButtonText, { color: palette.white }]}>
                Create
              </Text>
            </TouchableOpacity>
          </View>
        }
      >
        <View style={styles.modalFormContent}>
          <Text style={[styles.modalLabel, { color: colors.text }]}>
            Title:
          </Text>
          <TextInput
            style={[
              styles.formInput,
              {
                backgroundColor: colors.card,
                color: colors.text,
                borderColor: colors.border,
              },
            ]}
            value={announcementTitle}
            onChangeText={setAnnouncementTitle}
            placeholder="Announcement title"
            placeholderTextColor={colors.secondary}
          />
          <Text style={[styles.modalLabel, { color: colors.text }]}>
            Message:
          </Text>
          <TextInput
            style={[
              styles.formTextArea,
              {
                backgroundColor: colors.card,
                color: colors.text,
                borderColor: colors.border,
              },
            ]}
            value={announcementMessage}
            onChangeText={setAnnouncementMessage}
            placeholder="Announcement message"
            multiline
            numberOfLines={4}
            placeholderTextColor={colors.secondary}
          />
          {Platform.OS === 'web' && (
            <>
              <Text style={[styles.modalLabel, { color: colors.text }]}>
                Type:
              </Text>
              <select
                value={announcementType}
                onChange={(e) => setAnnouncementType(e.target.value)}
                style={{
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: `1px solid ${colors.border}`,
                  backgroundColor: colors.card,
                  color: colors.text,
                  fontSize: '14px',
                  marginBottom: 16,
                }}
              >
                <option value="info">Info</option>
                <option value="warning">Warning</option>
                <option value="error">Error</option>
                <option value="success">Success</option>
              </select>
            </>
          )}
          <Text style={[styles.modalLabel, { color: colors.text }]}>
            Start Date (optional):
          </Text>
          <TextInput
            style={[
              styles.formInput,
              {
                backgroundColor: colors.card,
                color: colors.text,
                borderColor: colors.border,
              },
            ]}
            value={announcementStartsAt}
            onChangeText={setAnnouncementStartsAt}
            placeholder="YYYY-MM-DDTHH:mm:ss (ISO format)"
            placeholderTextColor={colors.secondary}
          />
          <Text style={[styles.modalLabel, { color: colors.text }]}>
            End Date (optional):
          </Text>
          <TextInput
            style={[
              styles.formInput,
              {
                backgroundColor: colors.card,
                color: colors.text,
                borderColor: colors.border,
              },
            ]}
            value={announcementEndsAt}
            onChangeText={setAnnouncementEndsAt}
            placeholder="YYYY-MM-DDTHH:mm:ss (ISO format)"
            placeholderTextColor={colors.secondary}
          />
          <Text style={[styles.modalLabel, { color: colors.text }]}>
            Call to Action Text (optional):
          </Text>
          <TextInput
            style={[
              styles.formInput,
              {
                backgroundColor: colors.card,
                color: colors.text,
                borderColor: colors.border,
              },
            ]}
            value={announcementCtaText}
            onChangeText={setAnnouncementCtaText}
            placeholder="Button text"
            placeholderTextColor={colors.secondary}
          />
          <Text style={[styles.modalLabel, { color: colors.text }]}>
            Call to Action URL (optional):
          </Text>
          <TextInput
            style={[
              styles.formInput,
              {
                backgroundColor: colors.card,
                color: colors.text,
                borderColor: colors.border,
              },
            ]}
            value={announcementCtaUrl}
            onChangeText={setAnnouncementCtaUrl}
            placeholder="https://..."
            placeholderTextColor={colors.secondary}
          />
          <Text style={[styles.modalLabel, { color: colors.text }]}>
            Image URL (optional):
          </Text>
          <TextInput
            style={[
              styles.formInput,
              {
                backgroundColor: colors.card,
                color: colors.text,
                borderColor: colors.border,
              },
            ]}
            value={announcementImageUrl}
            onChangeText={setAnnouncementImageUrl}
            placeholder="https://..."
            placeholderTextColor={colors.secondary}
          />
        </View>
      </Modal>

      {/* FAQ Form Modal */}
      <Modal
        visible={faqFormVisible}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setFaqFormVisible(false)}
      >
        <View
          style={[
            styles.modalOverlay,
            { backgroundColor: 'rgba(0, 0, 0, 0.5)' },
          ]}
        >
          <View
            style={[
              styles.modalContent,
              { backgroundColor: colors.background },
            ]}
          >
            <View
              style={[styles.modalHeader, { borderBottomColor: colors.border }]}
            >
              <Text style={[styles.modalTitle, { color: colors.text }]}>
                {editingFaq ? 'Edit FAQ' : 'Create FAQ'}
              </Text>
              <TouchableOpacity onPress={() => setFaqFormVisible(false)}>
                <Ionicons name="close" size={24} color={colors.text} />
              </TouchableOpacity>
            </View>
            <Text style={[styles.modalLabel, { color: colors.text }]}>
              Question:
            </Text>
            <TextInput
              style={[
                styles.formInput,
                {
                  backgroundColor: colors.card,
                  color: colors.text,
                  borderColor: colors.border,
                },
              ]}
              value={faqQuestion}
              onChangeText={setFaqQuestion}
              placeholder="FAQ question"
              placeholderTextColor={colors.secondary}
            />
            <Text style={[styles.modalLabel, { color: colors.text }]}>
              Answer:
            </Text>
            <TextInput
              style={[
                styles.formTextArea,
                {
                  backgroundColor: colors.card,
                  color: colors.text,
                  borderColor: colors.border,
                },
              ]}
              value={faqAnswer}
              onChangeText={setFaqAnswer}
              placeholder="FAQ answer"
              multiline
              numberOfLines={6}
              placeholderTextColor={colors.secondary}
            />
            <Text style={[styles.modalLabel, { color: colors.text }]}>
              Category (optional):
            </Text>
            <TextInput
              style={[
                styles.formInput,
                {
                  backgroundColor: colors.card,
                  color: colors.text,
                  borderColor: colors.border,
                },
              ]}
              value={faqCategory}
              onChangeText={setFaqCategory}
              placeholder="Category"
              placeholderTextColor={colors.secondary}
            />
            {Platform.OS === 'web' && (
              <>
                <Text style={[styles.modalLabel, { color: colors.text }]}>
                  Order:
                </Text>
                <input
                  type="number"
                  value={faqOrder}
                  onChange={(e) => setFaqOrder(e.target.value)}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: `1px solid ${colors.border}`,
                    backgroundColor: colors.card,
                    color: colors.text,
                    fontSize: '14px',
                    marginBottom: 16,
                  }}
                />
              </>
            )}
            <View style={styles.modalActions}>
              <TouchableOpacity
                style={[
                  styles.modalButton,
                  { backgroundColor: colors.secondary },
                ]}
                onPress={() => setFaqFormVisible(false)}
              >
                <Text
                  style={[styles.modalButtonText, { color: palette.white }]}
                >
                  Cancel
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[
                  styles.modalButton,
                  { backgroundColor: colors.primary },
                ]}
                onPress={handleCreateFAQ}
              >
                <Text
                  style={[styles.modalButtonText, { color: palette.white }]}
                >
                  {editingFaq ? 'Update' : 'Create'}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </ScrollView>
  );
};

const _getStatusColor = (status: string) => {
  switch (status) {
    case 'open':
      return palette.brightBlue;
    case 'in_progress':
      return palette.lightOrange;
    case 'resolved':
      return palette.cyanLimeGreen;
    case 'closed':
      return palette.darkGrayBlue;
    default:
      return palette.darkGrayBlue;
  }
};

const getTypeColor = (type: string) => {
  switch (type) {
    case 'info':
      return palette.brightBlue;
    case 'warning':
      return palette.lightOrange;
    case 'error':
      return palette.brightMatteRed;
    case 'success':
      return palette.cyanLimeGreen;
    default:
      return palette.darkGrayBlue;
  }
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
  },
  tabs: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    marginBottom: 16,
  },
  tab: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    marginRight: 8,
  },
  tabText: {
    fontSize: 14,
    fontWeight: '600',
  },
  tabContent: {
    flex: 1,
  },
  searchBarContainer: {
    marginBottom: 16,
  },
  filterRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
    paddingBottom: 12,
    marginBottom: 16,
    borderBottomWidth: 1,
    alignItems: 'center',
  },
  filterGroup: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  filterLabel: {
    fontSize: 12,
    fontWeight: '600',
  },
  loading: {
    textAlign: 'center',
    marginTop: 20,
    fontSize: 14,
  },
  empty: {
    textAlign: 'center',
    marginTop: 20,
    fontSize: 14,
  },
  list: {
    gap: 12,
  },
  ticketCard: {
    padding: 16,
    borderRadius: 8,
    borderWidth: 1,
    marginBottom: 12,
  },
  ticketHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  ticketInfo: {
    flex: 1,
  },
  ticketSubject: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 4,
  },
  ticketMeta: {
    flexDirection: 'row',
  },
  ticketMetaText: {
    fontSize: 12,
  },
  ticketBadges: {
    flexDirection: 'row',
    gap: 8,
  },
  badge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
  },
  badgeText: {
    color: palette.white,
    fontSize: 11,
    fontWeight: '600',
  },
  ticketMessage: {
    fontSize: 14,
    marginBottom: 12,
    lineHeight: 20,
  },
  responseBox: {
    padding: 12,
    borderRadius: 6,
    marginBottom: 12,
  },
  responseLabel: {
    fontSize: 12,
    fontWeight: '600',
    marginBottom: 4,
  },
  responseText: {
    fontSize: 14,
    lineHeight: 20,
  },
  ticketActions: {
    flexDirection: 'row',
    gap: 8,
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 6,
  },
  actionButtonText: {
    color: palette.white,
    fontSize: 12,
    fontWeight: '600',
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '700',
  },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 6,
  },
  addButtonText: {
    fontSize: 12,
    fontWeight: '600',
  },
  announcementCard: {
    padding: 16,
    borderRadius: 8,
    borderWidth: 1,
    marginBottom: 12,
  },
  announcementHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  announcementTitle: {
    fontSize: 16,
    fontWeight: '600',
    flex: 1,
  },
  announcementMessage: {
    fontSize: 14,
    marginBottom: 8,
    lineHeight: 20,
  },
  announcementMeta: {
    fontSize: 12,
  },
  faqCard: {
    padding: 16,
    borderRadius: 8,
    borderWidth: 1,
    marginBottom: 12,
  },
  faqHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  faqQuestion: {
    fontSize: 16,
    fontWeight: '600',
    flex: 1,
    marginRight: 12,
  },
  faqActions: {
    flexDirection: 'row',
    gap: 8,
  },
  iconButton: {
    padding: 4,
  },
  faqAnswer: {
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 8,
  },
  faqCategory: {
    fontSize: 12,
  },
  modalOverlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    width: '100%',
    maxWidth: 600,
    borderRadius: 12,
    padding: 20,
    maxHeight: '90%',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: 12,
    borderBottomWidth: 1,
    marginBottom: 16,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '700',
  },
  modalLabel: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 8,
    marginTop: 12,
  },
  modalMessage: {
    fontSize: 14,
    marginBottom: 16,
    padding: 12,
    borderRadius: 6,
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
  },
  responseInput: {
    borderWidth: 1,
    borderRadius: 6,
    padding: 12,
    fontSize: 14,
    minHeight: 120,
    textAlignVertical: 'top',
  },
  formInput: {
    borderWidth: 1,
    borderRadius: 6,
    padding: 12,
    fontSize: 14,
    marginBottom: 16,
  },
  formTextArea: {
    borderWidth: 1,
    borderRadius: 6,
    padding: 12,
    fontSize: 14,
    minHeight: 100,
    textAlignVertical: 'top',
    marginBottom: 16,
  },
  modalActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 12,
    marginTop: 20,
  },
  modalButton: {
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 6,
  },
  modalButtonText: {
    fontSize: 14,
    fontWeight: '600',
  },
  modalFormContent: {
    gap: 8,
  },
});

export default SupportView;
